/**
 * Argalis V2 — L'encre LISIBLE à poser sur la couleur de marque d'un organisme.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * CE QUE CE MODULE REMPLACE, ET POURQUOI C'ÉTAIT FAUX
 * ═══════════════════════════════════════════════════════════════════════════
 * `brandTheme.foregroundFor` choisissait l'encre sur la **luminosité HSL** :
 * `l > 60 % ⇒ encre sombre, sinon blanc`. Sa docstring assumait l'approximation
 * — « suffisante pour un aperçu ». Sauf que la fonction n'habille plus un
 * aperçu : elle habille le portail réel du client (PORT / §24.5), et §24.5 dit
 * déjà que « le texte bascule blanc/encre selon la **luminance calculée** ».
 *
 * La luminosité HSL est la moyenne des extrêmes `(max + min) / 2`. Elle ignore
 * que l'œil voit le vert ~10× plus que le bleu. D'où trois échecs MESURÉS, tous
 * sous le seuil AA de 4,5:1 :
 *
 * | Marque    | Encre HSL rendue | Ratio obtenu | Bonne encre  | Ratio juste |
 * |-----------|------------------|--------------|--------------|-------------|
 * | `#FFD400` | blanc (L=50)     | **1,43**     | anthracite   | 12,16       |
 * | `#00B140` | blanc (L=35)     | **2,85**     | anthracite   | 6,10        |
 * | `#7C7C7C` | blanc (L=49)     | **4,17**     | noir         | 5,03        |
 *
 * Un jaune de marque rendait donc ses libellés QUASI INVISIBLES (1,43:1 —
 * pratiquement du blanc sur blanc), sur la seule page qui porte l'identité du
 * client.
 *
 * ⚠️ **Le gardien était VERT sur son propre défaut.** `brandTheme.test.ts` ne
 * testait que deux couleurs : `#FFE066` (L HSL = 70) et `#0B3D2E` (L HSL = 14).
 * Ce sont précisément les deux valeurs où la règle HSL tombe d'accord avec la
 * bonne réponse — l'une bien au-dessus du seuil, l'autre bien en dessous. Toute
 * la bande centrale (L HSL entre 30 et 60), où vit la majorité des chartes
 * saturées, n'était mesurée par personne. Le test choisi comme témoin était le
 * seul cas que la fonction traitait juste : c'est le motif à retenir, pas la
 * formule. D'où le balayage de `contrasteMarque.test.ts`, qui ne choisit rien.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * LA CASCADE (décision PO ③ du 2026-09-08)
 * ═══════════════════════════════════════════════════════════════════════════
 * blanc si ratio ≥ 4,5 ; sinon anthracite `#1A1A1A` si ratio ≥ 4,5 ; sinon noir.
 *
 * Pourquoi l'anthracite AVANT le noir : le noir pur sur un aplat coloré « troue »
 * la maquette, l'anthracite est l'encre de la charte Argalis. Et pourquoi le noir
 * quand même en dernier — **la cascade ne peut PAS échouer, c'est démontrable** :
 * si le blanc échoue, `1,05 / (L + 0,05) < 4,5` donc `L > 0,1833` ; le noir vaut
 * alors `(L + 0,05) / 0,05 > 4,667`, au-dessus du seuil. Autrement dit la
 * troisième branche est un filet qui ne peut pas céder, jamais un « au pire, tant
 * pis ». Vérifié par balayage : sur 1 048 576 couleurs, le pire ratio rendu est
 * exactement **4,5000** — la borne, atteinte, jamais franchie.
 *
 * La bande qui réclame le noir est étroite mais RÉELLE (les gris de `#777777` à
 * `#818181`, ~8 % d'un balayage uniforme de sRGB) : la retirer rendrait ~8 % des
 * chartes non conformes. Ne pas la simplifier.
 *
 * ⚠️ Ne PAS confondre la distribution d'un balayage uniforme (56 % anthracite,
 * 36 % blanc, 8 % noir) avec les « ~96 % d'anthracite » de la décision PO, qui
 * porte sur les chartes RÉELLES d'organismes de formation — deux populations
 * différentes. Ce module ne mesure que la première ; l'autre n'est pas de son
 * ressort.
 */

/** Seuil AA de WCAG 2.1 pour du texte normal (`DESIGN_SYSTEM.md`, §1514). */
export const SEUIL_CONTRASTE_AA = 4.5

/**
 * WCAG 2.1 §1.4.3 — le seuil du **GRAND texte** : ≥ 24 px, ou ≥ 18,66 px en
 * gras. C'est celui d'un titre posé sur un aplat de marque, jamais celui d'un
 * libellé de bouton.
 */
export const SEUIL_CONTRASTE_AA_GRAND = 3

/**
 * Le RÔLE du texte, qui décide du seuil — et donc, souvent, de l'encre.
 *
 * 🔴 **Pourquoi ce paramètre existe.** La cascade maximisait le ratio : sur le
 * magenta `#fc22cf` d'un organisme réel, elle rendait l'anthracite (5,20)
 * plutôt que le blanc (3,35). Le PO a objecté que le blanc était plus lisible
 * *et* plus beau — et il avait raison sur le fond : à 3,35 le blanc est
 * **conforme** dès que le texte est grand. Nous mesurions la même chose contre
 * deux seuils différents.
 *
 * La divergence n'est pas marginale : sur un balayage de 4 096 couleurs, le
 * sombre l'emporte au ratio alors que le blanc tient déjà 3:1 dans **21 %**
 * des cas. Un seul seuil trahissait donc un cinquième des chartes.
 *
 * ⚠️ `'grand'` n'autorise rien : il s'engage. Un texte déclaré grand DOIT
 * l'être à l'écran — c'est une promesse que le composant tient, pas une
 * dispense qu'il s'accorde.
 */
export type RoleDeTexte = 'normal' | 'grand'

/**
 * Le deuxième barreau de la cascade : un anthracite **d'encre**, arbitré par le
 * PO le 2026-09-09 (« anthracite et pas gris clair, gris sur jaune ne se lit
 * pas non plus ; le noir serait inélégant sur certaines couleurs »).
 *
 * ⚠️ **Ce n'est PAS l'anthracite de la charte**, qui vaut `#1F2A2E`
 * (`DESIGN_SYSTEM.md` §palette, jeton `--anthracite`). La valeur retenue
 * prolonge le `0 0% 10%` que `brandTheme` rendait déjà, et tient 17,4:1 sur
 * blanc contre 14,7:1 pour l'anthracite de charte. Le dire évite qu'un
 * relecteur « aligne sur la charte » en croyant corriger une dérive : ce
 * serait un changement de comportement, pas un nettoyage.
 */
export const ENCRE_ANTHRACITE = '#1A1A1A'

/** Les trois seules encres que le portail sait poser sur un aplat de marque. */
export type EncreDeMarque = '#FFFFFF' | typeof ENCRE_ANTHRACITE | '#000000'

const ENCRE_BLANCHE: EncreDeMarque = '#FFFFFF'
const ENCRE_NOIRE: EncreDeMarque = '#000000'

/**
 * `#12345` → `null`, `#abc` → `[170, 187, 204]`, `006A63` → `[0, 106, 99]`.
 *
 * Seul endroit du front qui décide de ce QU'EST un hex de marque acceptable.
 * `brandTheme.hexToHsl` s'appuie dessus : deux analyseurs jumeaux dériveraient,
 * et la garde finirait sur celui que personne n'emprunte.
 */
export function canauxSRGB(hex: string): [number, number, number] | null {
    const propre = hex.trim().replace(/^#/, '')
    const complet =
        propre.length === 3
            ? propre
                  .split('')
                  .map((c) => c + c)
                  .join('')
            : propre
    if (!/^[0-9a-fA-F]{6}$/.test(complet)) return null
    return [
        parseInt(complet.slice(0, 2), 16),
        parseInt(complet.slice(2, 4), 16),
        parseInt(complet.slice(4, 6), 16),
    ]
}

/**
 * Linéarisation d'un canal sRGB 0–255 (WCAG 2.1, « relative luminance »).
 *
 * ⚠️ L'étape que YIQ et HSL sautent, et c'est TOUTE la différence : un canal
 * sRGB est encodé en gamma ~2,2, la lumière qu'il représente n'est pas
 * proportionnelle à sa valeur. Sans cette étape, `#FFD400` « pèse » 0,5 alors
 * qu'il émet 0,68 de la lumière du blanc.
 *
 * Le seuil publié par WCAG 2.1 est 0,03928, celui de la spec sRGB 0,04045 (la
 * première est une coquille connue). Sans effet ici, et c'est MESURÉ : aucun des
 * 256 canaux 8 bits ne tombe entre les deux (le canal 10 vaut 0,0392, le 11 vaut
 * 0,0431) — les deux valeurs classent les 256 canaux à l'identique.
 */
function linearise(canal: number): number {
    const c = canal / 255
    return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
}

/** Luminance relative WCAG (0 = noir, 1 = blanc). `null` si ce n'est pas un hex. */
export function luminanceRelative(hex: string): number | null {
    const canaux = canauxSRGB(hex)
    if (!canaux) return null
    const [r, g, b] = canaux.map(linearise)
    // Coefficients WCAG 2.1 : le vert pèse 10× le bleu, c'est exactement ce que
    // la luminosité HSL ignorait.
    return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

/**
 * Ratio de contraste WCAG 2.1 entre deux couleurs, de 1 (identiques) à 21.
 *
 * ⚠️ **Rend `NaN` si l'une des deux n'est pas un hex exploitable**, et c'est
 * délibéré : toute comparaison `NaN >= seuil` vaut `false`, donc une couleur
 * inconnue n'est JAMAIS déclarée lisible par accident. Un `0` de repli aurait le
 * même effet sur `>=` mais mentirait sur les autres opérateurs — `NaN` se
 * reconnaît. Un appelant qui AFFICHE un ratio doit donc tester `Number.isFinite`.
 */
export function ratioDeContraste(hexA: string, hexB: string): number {
    const la = luminanceRelative(hexA)
    const lb = luminanceRelative(hexB)
    if (la === null || lb === null) return Number.NaN
    const clair = Math.max(la, lb)
    const sombre = Math.min(la, lb)
    return (clair + 0.05) / (sombre + 0.05)
}

/**
 * L'encre à poser sur un aplat de marque — cascade ③, garantie ≥ 4,5:1.
 *
 * ⚠️ **Encaisse n'importe quelle entrée sans lever** : `entities.primary_color`
 * n'est validé NULLE PART côté serveur (ni contrainte, ni schéma Pydantic), donc
 * `null`, `''`, `'bleu'` ou `'rgb(0,0,0)'` arrivent jusqu'ici. Une exception ici
 * ne dégraderait pas la couleur : elle rendrait la page de connexion BLANCHE,
 * pour un défaut de saisie.
 *
 * **Repli choisi : l'anthracite.** Justification — quand le hex est inexploitable,
 * aucun aplat de marque n'est peint : `brandThemeStyle` rend `undefined` et le
 * conteneur garde le thème par défaut, dont le fond est quasi blanc. Sur ce fond,
 * l'anthracite tient 17,4:1 tandis que le blanc tomberait à 1,0:1 — soit
 * l'invisibilité totale. Des deux échecs possibles, on prend systématiquement
 * celui qui reste lisible. C'est aussi le barreau modal de la cascade, donc le
 * repli est cohérent avec le rendu nominal plutôt qu'en rupture avec lui.
 *
 * La forme courte `#abc` n'est PAS traitée comme invalide : c'est une couleur CSS
 * légitime, la refuser rendrait l'anthracite de repli sur une charte parfaitement
 * exploitable. Elle est développée (`#abc` → `#aabbcc`) puis mesurée normalement.
 */
export function encreSurMarque(
    hexFond: string | null | undefined,
    role: RoleDeTexte = 'normal',
): EncreDeMarque {
    if (typeof hexFond !== 'string') return ENCRE_ANTHRACITE
    if (luminanceRelative(hexFond) === null) return ENCRE_ANTHRACITE

    const seuil = role === 'grand' ? SEUIL_CONTRASTE_AA_GRAND : SEUIL_CONTRASTE_AA

    // ⭐ Le blanc est essayé EN PREMIER, et gardé dès qu'il tient le seuil du
    // rôle — jamais « la meilleure des trois ». Sur une marque saturée, le
    // blanc lit comme la marque ; l'anthracite lit comme un pis-aller. On ne
    // maximise pas un chiffre, on tient un plancher.
    if (ratioDeContraste(hexFond, ENCRE_BLANCHE) >= seuil) return ENCRE_BLANCHE
    if (ratioDeContraste(hexFond, ENCRE_ANTHRACITE) >= seuil) return ENCRE_ANTHRACITE
    // Filet démontré non défaillant : voir l'en-tête du module. Si le blanc
    // échoue, la luminance du fond dépasse 0,1833 et le noir dépasse 4,667:1.
    return ENCRE_NOIRE
}
